/*
 * Hearthstone-style card text validator.
 *
 * Backwards compatible with the old API:
 *   getMistakes(cardParams) -> Array<{ id, message, fixLabel, apply }>
 *
 * Extra metadata (`field` and `severity`) is aditive and can be ignored by
 * existing consumers. Every automatic fix is applied to the latest card
 * object, so fixes remain safe when users apply several suggestions in a row.
 */

const FORMAT_TAG_SOURCE = String.raw`\[\/?(?:b|i)\]`;
const FORMAT_TAG_REGEX = /\[\/?(?:b|i)\]/gi;

const MINION_TYPES = [
  'All',
  'Beast',
  'Demon',
  'Dragon',
  'Draenei',
  'Elemental',
  'Mech',
  'Murloc',
  'Naga',
  'Pirate',
  'Quilboar',
  'Totem',
  'Undead'
];

const SPELL_SCHOOLS = ['Arcane', 'Fel', 'Fire', 'Frost', 'Holy', 'Nature', 'Shadow'];

const MINION_TYPE_PLURALS = Object.freeze({
  Beast: 'Beasts',
  Demon: 'Demons',
  Dragon: 'Dragons',
  Draenei: 'Draenei',
  Elemental: 'Elementals',
  Mech: 'Mechs',
  Murloc: 'Murlocs',
  Naga: 'Naga',
  Pirate: 'Pirates',
  Quilboar: 'Quilboar',
  Totem: 'Totems',
  Undead: 'Undead'
});

// Only nouns with stable Hearthstone meanings belong here. A controlled list
// avoids generic English inflection mistakes around card names and tokens.
const COUNT_NOUN_FORMS = Object.freeze([
  Object.freeze({ singular: 'Mana Crystal', plural: 'Mana Crystals' }),
  Object.freeze({ singular: 'Corpse', plural: 'Corpses' }),
  Object.freeze({ singular: 'Secret', plural: 'Secrets' }),
  Object.freeze({ singular: 'card', plural: 'cards' }),
  Object.freeze({ singular: 'minion', plural: 'minions' }),
  Object.freeze({ singular: 'spell', plural: 'spells' }),
  Object.freeze({ singular: 'weapon', plural: 'weapons' }),
  Object.freeze({ singular: 'hero', plural: 'heroes' }),
  Object.freeze({ singular: 'character', plural: 'characters' }),
  Object.freeze({ singular: 'turn', plural: 'turns' }),
  Object.freeze({ singular: 'player', plural: 'players' }),
  Object.freeze({ singular: 'opponent', plural: 'opponents' }),
  Object.freeze({ singular: 'copy', plural: 'copies' }),
  ...Object.entries(MINION_TYPE_PLURALS).map(([singular, plural]) => (
    Object.freeze({ singular, plural })
  ))
]);

const COUNT_WORD_VALUES = Object.freeze({
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10
});

const CLASSES = [
  'Neutral',
  'DeathKnight',
  'DemonHunter',
  'Druid',
  'Hunter',
  'Mage',
  'Paladin',
  'Priest',
  'Rogue',
  'Shaman',
  'Warlock',
  'Warrior'
];

// Includes current and historical Constructed keywords/mechanics. Keeping the
// vocabulary broad is useful for Wild cards and custom expansions.
const KEYWORDS = [
  'Adapt',
  'Avenge',
  'Battlecry',
  'Bounty',
  'Charge',
  'Choose One',
  'Chromadrake',
  'Colossal',
  'Combo',
  'Corrupt',
  'Dark Gift',
  'Deathrattle',
  'Discover',
  'Divine Shield',
  'Dormant',
  'Dredge',
  'Echo',
  'Elusive',
  'Enrage',
  'Excavate',
  'Fabled',
  'Finale',
  'Fodder',
  'Forge',
  'Freeze',
  'Frozen',
  'Frenzy',
  'Gigantify',
  'Herald',
  'Honorable Kill',
  'Imbue',
  'Immune',
  'Infuse',
  'Inspire',
  'Invoke',
  'Kindred',
  'Lifesteal',
  'Magnetic',
  'Manathirst',
  'Mega-Windfury',
  'Miniaturize',
  'Outcast',
  'Overheal',
  'Overkill',
  'Overload',
  'Poisonous',
  'Prepare',
  'Quickdraw',
  'Quest',
  'Questline',
  'Rally',
  'Reborn',
  'Recruit',
  'Reward',
  'Rewind',
  'Rush',
  'Secret',
  'Shatter',
  'Silence',
  'Spell Damage',
  'Spellcraft',
  'Spellburst',
  'Starship',
  'Starship Piece',
  'Start of Game',
  'Stealth',
  'Taunt',
  'Titan',
  'Tourist',
  'Tradeable',
  'Twinspell',
  'Windfury'
];

// These mechanics conventionally introduce an effect with a colon.
const COLON_KEYWORDS = [
  'Battlecry',
  'Combo',
  'Deathrattle',
  'Finale',
  'Forge',
  'Frenzy',
  'Honorable Kill',
  'Kindred',
  'Manathirst',
  'Outcast',
  'Overheal',
  'Overkill',
  'Quickdraw',
  'Spellburst'
];

const PASSIVE_KEYWORDS = [
  'Charge',
  'Divine Shield',
  'Elusive',
  'Immune',
  'Lifesteal',
  'Mega-Windfury',
  'Poisonous',
  'Reborn',
  'Rush',
  'Stealth',
  'Taunt',
  'Tradeable',
  'Windfury'
];

// On a Minion, these verbs normally describe an immediate one-shot effect and
// therefore need a play/trigger condition such as Battlecry or Deathrattle.
// This list is intentionally narrow to avoid guessing at continuous effects.
const IMMEDIATE_EFFECT_VERBS = [
  'Add',
  'Cast',
  'Copy',
  'Deal',
  'Destroy',
  'Discover',
  'Draw',
  'Dredge',
  'Equip',
  'Excavate',
  'Freeze',
  'Gain',
  'Give',
  'Restore',
  'Set',
  'Shuffle',
  'Silence',
  'Steal',
  'Summon',
  'Transform'
];

const SMALL_TITLE_WORDS = new Set([
  'a', 'an', 'and', 'at', 'but', 'by', 'for', 'from', 'in', 'nor', 'of',
  'on', 'or', 'the', 'to', 'vs', 'with'
]);

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const makeRegex = (regex, forceGlobal = false) => {
  const flags = forceGlobal && !regex.flags.includes('g') ? `${regex.flags}g` : regex.flags;
  return new RegExp(regex.source, flags);
};

const hasMatch = (value, regex) => makeRegex(regex).test(value);

const replaceRegex = (value, regex, replacement) => value.replace(makeRegex(regex, true), replacement);

const stripFormatting = (value) => value.replace(FORMAT_TAG_REGEX, '');

const BANNER_KEYWORDS = Object.freeze([
  Object.freeze({ keyword: 'Tradeable', banner: 'banner_tradeable.webp' }),
  Object.freeze({ keyword: 'Prepare', banner: 'banner_prepare.webp' }),
  Object.freeze({ keyword: 'Forge', banner: 'banner_forge.webp' })
]);

// A mention such as "Tradeable cards cost (1) less" does not make this card
// Tradeable. Passive banner keywords must lead the card as their own marker.
// Forge can also appear as an official "Forge: ..." clause after another
// sentence, as on cards such as Eulogizer.
const hasOwnBannerKeyword = (value, keyword) => {
  const visible = stripFormatting(String(value ?? '')).trimStart();
  if (!visible) return false;

  const escapedKeyword = escapeRegExp(keyword);
  if (keyword === 'Forge') {
    return new RegExp(
      String.raw`(?:^|[.!?]\s+|\n)\s*${escapedKeyword}(?=\s*:|\s*$|\s*[,.](?:\s|$))`,
      'i'
    ).test(visible);
  }

  return new RegExp(
    String.raw`^${escapedKeyword}(?=\s*$|\s*[\n,.])`,
    'i'
  ).test(visible);
};

const normalizeLookup = (value) => value.toLocaleLowerCase('en-US').replace(/[\s_-]+/g, ' ').trim();

const canonicalMap = (values) => new Map(values.map((value) => [normalizeLookup(value), value]));

const countNounFormMap = new Map(COUNT_NOUN_FORMS.flatMap((forms) => [
  [normalizeLookup(forms.singular), forms],
  [normalizeLookup(forms.plural), forms]
]));

const countNounAlternation = [...new Set(COUNT_NOUN_FORMS.flatMap(({ singular, plural }) => (
  [singular, plural]
)))]
  .sort((a, b) => b.length - a.length)
  .map(escapeRegExp)
  .join('|');

const countValueAlternation = String.raw`\d+|${Object.keys(COUNT_WORD_VALUES).join('|')}`;
const countModifierSource = String.raw`random|friendly|enemy|other|different|adjacent|damaged|undamaged|frozen|lowest-cost|highest-cost|\d+-cost`;
const countNounGapSource = String.raw`((?:[ \t]+(?:${countModifierSource}))*[ \t]+)`;
// Do not change the adjectival noun in compounds such as "minion types",
// "spell schools", "Hero Powers", or "card packs".
const countNounTrailerExclusion = String.raw`(?![ \t]+(?:cards?|types?|schools?|powers?|packs?)\b)`;

const countedNounRegex = new RegExp(
  String.raw`\b(${countValueAlternation})${countNounGapSource}(${countNounAlternation})\b${countNounTrailerExclusion}`,
  'gi'
);
const singularQuantifierNounRegex = new RegExp(
  String.raw`\b(a|an|each|every)${countNounGapSource}(${countNounAlternation})\b${countNounTrailerExclusion}`,
  'gi'
);
const pluralQuantifierNounRegex = new RegExp(
  String.raw`\b(both|all)${countNounGapSource}(${countNounAlternation})\b${countNounTrailerExclusion}`,
  'gi'
);

const countValue = (rawValue) => {
  if (/^\d+$/.test(rawValue)) return Number(rawValue);
  return COUNT_WORD_VALUES[rawValue.toLocaleLowerCase('en-US')];
};

const expectedCountNoun = (noun, usePlural) => {
  const forms = countNounFormMap.get(normalizeLookup(noun));
  if (!forms) return noun;
  const expected = usePlural ? forms.plural : forms.singular;
  // Capitalization has its own validator rules. Do not report a plural error
  // when the noun already has the correct number but only the wrong case.
  return normalizeLookup(noun) === normalizeLookup(expected) ? noun : expected;
};

const getCountNounAgreementResult = (value) => {
  const corrections = [];
  const track = (from, to) => {
    if (from !== to) corrections.push({ from, to });
    return to;
  };

  let fixedText = String(value);
  fixedText = fixedText.replace(countedNounRegex, (full, rawCount, gap, noun) => track(
    full,
    `${rawCount}${gap}${expectedCountNoun(noun, countValue(rawCount) !== 1)}`
  ));
  fixedText = fixedText.replace(
    singularQuantifierNounRegex,
    (full, quantifier, gap, noun) => track(
      full,
      `${quantifier}${gap}${expectedCountNoun(noun, false)}`
    )
  );
  fixedText = fixedText.replace(
    pluralQuantifierNounRegex,
    (full, quantifier, gap, noun) => track(
      full,
      `${quantifier}${gap}${expectedCountNoun(noun, true)}`
    )
  );

  return { text: fixedText, corrections };
};

const normalizeCountNounAgreement = (value) => getCountNounAgreementResult(value).text;

const countMatches = (value, regex) => [...value.matchAll(makeRegex(regex, true))].length;

const applyField = (field, transform) => (params) => ({
  ...params,
  [field]: transform(String(params[field] ?? ''))
});

const titleCaseSuggestion = (title) => {
  const words = title.trim().replace(/[ \t]{2,}/g, ' ').split(' ');
  let startOfSubtitle = true;

  return words.map((word, index) => {
    if (!word) return word;

    const lower = word.toLocaleLowerCase('en-US');
    const lettersOnly = word.replace(/[^\p{L}]/gu, '');
    const isAllLower = lettersOnly && lettersOnly === lettersOnly.toLocaleLowerCase('en-US');
    const isConnector = index > 0 && !startOfSubtitle && SMALL_TITLE_WORDS.has(lower);
    let fixed = word;

    if (isConnector) {
      fixed = lower;
    } else if (isAllLower) {
      fixed = word.replace(/\p{L}/u, (letter) => letter.toLocaleUpperCase('en-US'));
    }

    startOfSubtitle = /[:—–-]$/.test(word);
    return fixed;
  }).join(' ');
};

const parseFormattingTags = (text) => {
  const stack = [];
  const errors = [];
  const regex = /\[(\/)?(b|i)\]/gi;

  for (const match of text.matchAll(regex)) {
    const closing = Boolean(match[1]);
    const tag = match[2].toLocaleLowerCase('en-US');

    if (!closing) {
      stack.push({ tag, index: match.index });
      continue;
    }

    const open = stack.pop();
    if (!open || open.tag !== tag) {
      errors.push(match[0]);
      if (open) stack.push(open);
    }
  }

  errors.push(...stack.map(({ tag }) => `[${tag}]`));
  return errors;
};

const TEXT_REPLACEMENTS = [
  { id: 'mojibake_acute', regex: /\u00C3\u201A[\u00C2\u00B4']/g, replacement: "'", label: 'Replace broken apostrophe encoding' },
  { id: 'mojibake_left_quote', regex: /\u00C3\u00A2\u00E2\u201A\u00AC\u00CB\u0153/g, replacement: "'", label: 'Replace broken apostrophe encoding' },
  { id: 'mojibake_right_quote', regex: /\u00C3\u00A2\u00E2\u201A\u00AC\u00E2\u201E\u00A2/g, replacement: "'", label: 'Replace broken apostrophe encoding' },
  { id: 'battlecry_spelling', regex: /\bbattle[ \t]+cry\b/gi, replacement: 'Battlecry', label: 'Use “Battlecry”' },
  { id: 'deathrattle_spelling', regex: /\bdeath[ \t]+rattle\b/gi, replacement: 'Deathrattle', label: 'Use “Deathrattle”' },
  { id: 'lifesteal_spelling', regex: /\blife[ \t]+steal\b/gi, replacement: 'Lifesteal', label: 'Use “Lifesteal”' },
  { id: 'whenever_spelling', regex: /\bwhen\s+ever\b/gi, replacement: 'Whenever', label: 'Use “Whenever”' },
  { id: 'every_time_wording', regex: /\bevery time\b/gi, replacement: 'Whenever', label: 'Use “Whenever”' },
  { id: 'damage_spelling', regex: /\b(?:dammage|damge)\b/gi, replacement: 'damage', label: 'Correct “damage”' },
  { id: 'damaged_spelling', regex: /\bdameged\b/gi, replacement: 'damaged', label: 'Correct “damaged”' },
  { id: 'double_spelling', regex: /\bdubble\b/gi, replacement: 'double', label: 'Correct “double”' },
  { id: 'enemy_spelling', regex: /\b(?:ennemy|enmies)\b/gi, replacement: (word) => word.toLocaleLowerCase('en-US') === 'enmies' ? 'enemies' : 'enemy', label: 'Correct “enemy/enemies”' },
  { id: 'attack_spelling', regex: /\b(?:atack|attacket)\b/gi, replacement: (word) => word.toLocaleLowerCase('en-US') === 'attacket' ? 'attacked' : 'attack', label: 'Correct “attack/attacked”' },
  { id: 'random_spelling', regex: /\brandon\b/gi, replacement: 'random', label: 'Correct “random”' },
  { id: 'their_spelling', regex: /\bthier\b/gi, replacement: 'their', label: 'Correct “their”' },
  { id: 'heroes_spelling', regex: /\bheros\b/gi, replacement: 'heroes', label: 'Correct “heroes”' },
  { id: 'weapon_spelling', regex: /\bwepon\b/gi, replacement: 'weapon', label: 'Correct “weapon”' },
  { id: 'youre_apostrophe', regex: /\byoure\b/gi, replacement: "you're", label: 'Use “you’re”' },
  { id: 'if_holding_grammar', regex: /\bif you holding\b/gi, replacement: "if you're holding", label: 'Use “if you’re holding”' },
  { id: 'if_have_grammar', regex: /\bif you has\b/gi, replacement: 'if you have', label: 'Use “if you have”' },
  { id: 'enemy_minions_grammar', regex: /\benemies minions\b/gi, replacement: 'enemy minions', label: 'Use “enemy minions”' },
  { id: 'this_takes_damage_grammar', regex: /\bwhenever this take damage\b/gi, replacement: 'Whenever this takes damage', label: 'Use “Whenever this takes damage”' },
  { id: 'a_enemy_article', regex: /\ba (enemy|empty)\b/gi, replacement: (full, word) => `an ${word.toLocaleLowerCase('en-US')}`, label: 'Use the correct article' },
  { id: 'an_random_article', regex: /\ban random\b/gi, replacement: 'a random', label: 'Use “a random”' },
  { id: 'a_elemental_article', regex: /\ba elemental\b/gi, replacement: 'an Elemental', label: 'Use “an Elemental”' },
  { id: 'cthun_capitalization', regex: /\bc['’]thun\b/gi, replacement: "C'Thun", label: 'Use “C’Thun”' },
  { id: 'both_players_possessive', regex: /\bboth player's\b/gi, replacement: "both players'", label: 'Use the plural possessive “players’”' },
  { id: 'unit_wording', regex: /\bunits?\b/gi, replacement: (word) => /^unit$/i.test(word) ? 'character' : 'characters', label: 'Use Hearthstone’s “character” wording' }
];

export const getMistakes = (cardParams = {}) => {
  const mistakes = [];
  const seenIds = new Set();
  const text = String(cardParams.Text ?? '');
  const title = String(cardParams.Title ?? '');

  const addMistake = ({
    id,
    message,
    fixLabel = null,
    apply = null,
    field = 'Text',
    severity = 'warning'
  }) => {
    if (seenIds.has(id)) return;
    seenIds.add(id);
    mistakes.push({ id, message, fixLabel, apply, field, severity });
  };

  const addTextFix = ({ id, regex, replacement, message, fixLabel, severity = 'error' }) => {
    if (!hasMatch(text, regex)) return;
    const fixedText = replaceRegex(text, regex, replacement);
    if (fixedText === text) return;
    const occurrences = countMatches(text, regex);
    addMistake({
      id,
      message: occurrences > 1 ? `${message} (${occurrences} occurrences)` : message,
      fixLabel,
      apply: applyField('Text', (value) => replaceRegex(value, regex, replacement)),
      severity
    });
  };

  // Formatting tags are validated before prose rules so malformed markup does
  // not silently produce misleading fixes.
  const tagErrors = parseFormattingTags(text);
  if (tagErrors.length) {
    addMistake({
      id: 'unbalanced_formatting_tags',
      message: `Unbalanced or incorrectly nested formatting tag${tagErrors.length === 1 ? '' : 's'}: ${tagErrors.join(', ')}.`,
      field: 'Text',
      severity: 'error'
    });
  }

  if (text !== text.trim()) {
    addMistake({
      id: 'trim_text',
      message: 'Card text has leading or trailing whitespace.',
      fixLabel: 'Trim card text',
      apply: applyField('Text', (value) => value.trim()),
      severity: 'error'
    });
  }

  addTextFix({
    id: 'repeated_horizontal_space',
    regex: /[ \t]{2,}/g,
    replacement: ' ',
    message: 'Card text contains repeated horizontal whitespace.',
    fixLabel: 'Collapse repeated spaces'
  });

  for (const rule of TEXT_REPLACEMENTS) {
    addTextFix({
      ...rule,
      message: 'Card text contains a typo or nonstandard Hearthstone wording.',
      fixLabel: rule.label
    });
  }

  const countAgreement = getCountNounAgreementResult(text);
  if (countAgreement.text !== text) {
    const [firstCorrection] = countAgreement.corrections;
    const correctionPreview = `"${firstCorrection.from}" \u2192 "${firstCorrection.to}"`;
    const remainingCorrections = countAgreement.corrections.length - 1;
    const remainingLabel = remainingCorrections
      ? ` (+${remainingCorrections} more change${remainingCorrections === 1 ? '' : 's'})`
      : '';

    addMistake({
      id: 'count_noun_agreement',
      message: 'Incorrect singular/plural agreement.',
      fixLabel: `${correctionPreview}${remainingLabel}`,
      apply: applyField('Text', normalizeCountNounAgreement),
      severity: 'error'
    });
  }

  addTextFix({
    id: 'space_before_punctuation',
    regex: /[ \t]+([,.;:!?])/g,
    replacement: '$1',
    message: 'There is a space before punctuation.',
    fixLabel: 'Remove spaces before punctuation'
  });

  addTextFix({
    id: 'missing_space_after_punctuation',
    regex: new RegExp(String.raw`([,;:!?]|(?<!\b[A-Za-z])\.(?!\.))(?=(?:${FORMAT_TAG_SOURCE})*\p{L})`, 'gu'),
    replacement: '$1 ',
    message: 'Punctuation must be followed by a space.',
    fixLabel: 'Add spaces after punctuation'
  });

  if (/\(\s+[+-]?\d+|[+-]?\d+\s+\)/g.test(text)) {
    addMistake({
      id: 'numeric_parenthesis_spacing',
      message: 'Numeric reminder values have extra spacing inside parentheses.',
      fixLabel: 'Tighten numeric parentheses',
      apply: applyField('Text', (value) => value.replace(/\(\s*([+-]?\d+)\s*\)/g, '($1)')),
      severity: 'error'
    });
  }

  addTextFix({
    id: 'stat_slash_spacing',
    regex: /([+-]\d+)\s+\/\s*([+-]\d+)|([+-]\d+)\s*\/\s+([+-]\d+)/g,
    replacement: (full) => full.replace(/\s*\/\s*/g, '/'),
    message: 'Stat pairs should not contain spaces around the slash.',
    fixLabel: 'Use compact stat pairs such as “+1/+1”'
  });

  addTextFix({
    id: 'space_inside_signed_number',
    regex: /([+-])\s+(\d+)/g,
    replacement: '$1$2',
    message: 'A plus or minus sign must touch its number.',
    fixLabel: 'Use compact signed numbers such as “+1”'
  });

  addTextFix({
    id: 'choose_one_separator',
    regex: /\bchoose\s+(?:one|1)\s*[:\-]\s*/gi,
    replacement: 'Choose One - ',
    message: 'Choose One uses a spaced hyphen, not a colon or numeral.',
    fixLabel: 'Use “Choose One - ”'
  });

  addTextFix({
    id: 'start_of_game_wording',
    regex: /\bstart\s+of\s+(?:the\s+)?game\s*:/gi,
    replacement: 'Start of Game:',
    message: 'The established trigger is “Start of Game:”.',
    fixLabel: 'Use “Start of Game:”'
  });

  addTextFix({
    id: 'forge_separator',
    regex: /\bforge\s*[-–—]\s*/gi,
    replacement: 'Forge: ',
    message: 'A Forge effect is introduced with a colon.',
    fixLabel: 'Use “Forge: ”'
  });

  addTextFix({
    id: 'spell_damage_number_spacing',
    regex: /\bspell damage\s*\+\s*(\d+)/gi,
    replacement: 'Spell Damage +$1',
    message: 'Spell Damage uses the format “Spell Damage +X”.',
    fixLabel: 'Normalize Spell Damage formatting'
  });

  addTextFix({
    id: 'colossal_number_format',
    regex: /\bcolossal\s*(?:\(\s*(\d+)\s*\)|\+\s*(\d+))/gi,
    replacement: (full, parenthesized, signed) => `Colossal +${parenthesized ?? signed}`,
    message: 'Colossal uses the format “Colossal +X”.',
    fixLabel: 'Normalize Colossal formatting'
  });

  addTextFix({
    id: 'overload_number_format',
    regex: /\boverload\s*:?\s*(?:\(\s*(\d+)\s*\)|(\d+))/gi,
    replacement: (full, parenthesized, bare) => `Overload: (${parenthesized ?? bare})`,
    message: 'Overload uses the format “Overload: (X)”.',
    fixLabel: 'Normalize Overload formatting'
  });

  addTextFix({
    id: 'numeric_cost_case',
    regex: /\b(\d+)-cost\b/gi,
    replacement: '$1-Cost',
    message: 'Numeric card costs use a capitalized “Cost”, such as “1-Cost”.',
    fixLabel: 'Capitalize “Cost”'
  });

  addTextFix({
    id: 'numbered_trigger_format',
    regex: /\b(infuse|manathirst)\s*(?:\(\s*(\d+)\s*\)|(\d+))\s*:/gi,
    replacement: (full, keyword, parenthesized, bare) => {
      const canonical = keyword[0].toLocaleUpperCase('en-US') + keyword.slice(1).toLocaleLowerCase('en-US');
      return `${canonical} (${parenthesized ?? bare}):`;
    },
    message: 'Numbered keywords use the format “Keyword (X):”.',
    fixLabel: 'Normalize the numbered keyword'
  });

  addTextFix({
    id: 'mana_cost_parentheses',
    regex: /\b(costs?)\s+(\d+)\s+(less|more)\b/gi,
    replacement: '$1 ($2) $3',
    message: 'Mana cost adjustments put the number in parentheses.',
    fixLabel: 'Use “costs (X) less/more”'
  });

  // Capitalize only the first real text character, not manually wrapped lines.
  const lowerTextStart = new RegExp(String.raw`^((?:(?:${FORMAT_TAG_SOURCE})|\s)*)(\p{Ll})`, 'u');
  if (lowerTextStart.test(text)) {
    addMistake({
      id: 'lowercase_text_start',
      message: 'Card text starts with a lowercase letter.',
      fixLabel: 'Capitalize the first letter',
      apply: applyField('Text', (value) => value.replace(
        lowerTextStart,
        (full, prefix, letter) => prefix + letter.toLocaleUpperCase('en-US')
      )),
      severity: 'error'
    });
  }

  const sentenceStartRegex = new RegExp(
    String.raw`([.!?])((?:(?:${FORMAT_TAG_SOURCE})|\s)+)(\p{Ll})`,
    'gu'
  );
  if (sentenceStartRegex.test(text)) {
    addMistake({
      id: 'lowercase_sentence_start',
      message: 'A sentence starts with a lowercase letter.',
      fixLabel: 'Capitalize sentence starts',
      apply: applyField('Text', (value) => value.replace(
        sentenceStartRegex,
        (full, punctuation, gap, letter) => punctuation + gap + letter.toLocaleUpperCase('en-US')
      )),
      severity: 'error'
    });
  }

  const colonEffectStartRegex = new RegExp(
    String.raw`\b(${COLON_KEYWORDS.map(escapeRegExp).join('|')})\s*:((?:(?:${FORMAT_TAG_SOURCE})|\s)+)(\p{L})`,
    'giu'
  );
  const hasLowercaseKeywordEffect = [...text.matchAll(colonEffectStartRegex)].some((match) => (
    match[3] === match[3].toLocaleLowerCase('en-US') &&
    match[3] !== match[3].toLocaleUpperCase('en-US')
  ));
  if (hasLowercaseKeywordEffect) {
    addMistake({
      id: 'lowercase_keyword_effect_start',
      message: 'An effect after a keyword colon starts with a lowercase letter.',
      fixLabel: 'Capitalize the effect',
      apply: applyField('Text', (value) => value.replace(
        colonEffectStartRegex,
        (full, keyword, gap, letter) => {
          const isLowercase = letter === letter.toLocaleLowerCase('en-US') &&
            letter !== letter.toLocaleUpperCase('en-US');
          return `${keyword}:${gap}${isLowercase ? letter.toLocaleUpperCase('en-US') : letter}`;
        }
      )),
      severity: 'error'
    });
  }

  // Card text is not flavor text: when an exact official keyword appears, use
  // its canonical capitalization. Longest-first matching keeps phrases such as
  // "Starship Piece" and "Divine Shield" together as one keyword.
  const keywordMap = canonicalMap(KEYWORDS);
  const canonicalKeywordRegex = new RegExp(
    String.raw`\b(${[...KEYWORDS].sort((a, b) => b.length - a.length).map(escapeRegExp).join('|')})\b`,
    'gi'
  );
  const hasWrongKeywordCase = [...text.matchAll(canonicalKeywordRegex)].some((match) => (
    match[0] !== keywordMap.get(normalizeLookup(match[0]))
  ));

  if (hasWrongKeywordCase) {
    addMistake({
      id: 'keyword_capitalization',
      message: 'One or more Hearthstone keywords use nonstandard capitalization.',
      fixLabel: 'Capitalize official keywords',
      apply: applyField('Text', (value) => value.replace(
        canonicalKeywordRegex,
        (keyword) => keywordMap.get(normalizeLookup(keyword)) ?? keyword
      )),
      severity: 'error'
    });
  }

  for (const canonical of MINION_TYPES.filter((type) => type !== 'All')) {
    const plural = MINION_TYPE_PLURALS[canonical];
    const regex = new RegExp(String.raw`\b${escapeRegExp(canonical)}s?\b`, 'gi');
    const wrongMatches = [...text.matchAll(makeRegex(regex, true))].filter((match) => {
      const found = match[0].toLocaleLowerCase('en-US');
      const expected = found === canonical.toLocaleLowerCase('en-US') ? canonical : plural;
      return match[0] !== expected;
    });
    if (!wrongMatches.length) continue;

    addMistake({
      id: `minion_type_case_${canonical.toLocaleLowerCase('en-US')}`,
      message: `Minion types such as “${canonical}” are capitalized in card text.`,
      fixLabel: `Capitalize “${canonical}”`,
      apply: applyField('Text', (value) => value.replace(regex, (found) => {
        const normalized = found.toLocaleLowerCase('en-US');
        return normalized === canonical.toLocaleLowerCase('en-US') ? canonical : plural;
      })),
      severity: 'error'
    });
  }

  for (const school of SPELL_SCHOOLS) {
    const regex = new RegExp(String.raw`\b${escapeRegExp(school)}(?=\s+spells?\b)`, 'gi');
    const wrongCase = [...text.matchAll(makeRegex(regex, true))].some((match) => match[0] !== school);
    if (!wrongCase) continue;

    addMistake({
      id: `spell_school_case_${school.toLocaleLowerCase('en-US')}`,
      message: `Spell schools such as “${school}” are capitalized.`,
      fixLabel: `Capitalize “${school}”`,
      apply: applyField('Text', (value) => value.replace(regex, school)),
      severity: 'error'
    });
  }

  const gameTermRules = [
    { id: 'hero_power_case', regex: /\bhero power\b/gi, canonical: 'Hero Power' },
    { id: 'health_case', regex: /\bhealth\b/gi, canonical: 'Health' },
    { id: 'armor_case', regex: /\barmor\b/gi, canonical: 'Armor' },
    { id: 'mana_case', regex: /\bmana\b/gi, canonical: 'Mana' }
  ];

  for (const { id, regex, canonical } of gameTermRules) {
    const wrongCase = [...text.matchAll(makeRegex(regex, true))].some((match) => match[0] !== canonical);
    if (!wrongCase) continue;
    addMistake({
      id,
      message: `The game term “${canonical}” is capitalized.`,
      fixLabel: `Use “${canonical}”`,
      apply: applyField('Text', (value) => value.replace(regex, canonical)),
      severity: 'error'
    });
  }

  const statAttackRegex = /((?:[+-]?\d+|that much|its|this minion's)\s+)attack\b/gi;
  if (hasMatch(text, statAttackRegex)) {
    addMistake({
      id: 'attack_stat_case',
      message: '“Attack” is capitalized when it refers to the stat.',
      fixLabel: 'Capitalize the Attack stat',
      apply: applyField('Text', (value) => value.replace(statAttackRegex, '$1Attack')),
      severity: 'error'
    });
  }

  const manualKeywordRegex = new RegExp(
    String.raw`\[b\](${[...KEYWORDS].sort((a, b) => b.length - a.length).map(escapeRegExp).join('|')})\[\/b\]`,
    'gi'
  );
  if (hasMatch(text, manualKeywordRegex)) {
    addMistake({
      id: 'manual_bold_keyword',
      message: 'Official keywords are auto-bolded and should not have manual bold tags.',
      fixLabel: 'Remove manual keyword bolding',
      apply: applyField('Text', (value) => value.replace(
        manualKeywordRegex,
        (full, keyword) => keywordMap.get(normalizeLookup(keyword)) ?? keyword
      )),
      severity: 'warning'
    });
  }

  // Consecutive, correctly capitalized passive keywords are unambiguous. Use a
  // comma, matching Hearthstone's compact keyword-list style.
  const passiveAlternation = [...PASSIVE_KEYWORDS]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|');
  const adjacentPassiveRegex = new RegExp(
    String.raw`\b(${passiveAlternation})\b((?:${FORMAT_TAG_SOURCE})*)[ \t]+((?:${FORMAT_TAG_SOURCE})*)\b(${passiveAlternation})\b`,
    'g'
  );
  const adjacentPassive = [...text.matchAll(adjacentPassiveRegex)];
  if (adjacentPassive.length) {
    addMistake({
      id: 'adjacent_passive_keywords',
      message: `Adjacent keywords need a separator: “${adjacentPassive[0][1]} ${adjacentPassive[0][4]}”.`,
      fixLabel: 'Separate adjacent keywords with commas',
      apply: applyField('Text', (value) => {
        let previous;
        let fixed = value;

        // Repeat so "Taunt Charge Rush" is corrected in one click.
        do {
          previous = fixed;
          fixed = fixed.replace(
            adjacentPassiveRegex,
            (full, first, trailingTags, leadingTags, second) => (
              `${first}${trailingTags}, ${leadingTags}${second}`
            )
          );
        } while (fixed !== previous);

        return fixed;
      }),
      severity: 'error'
    });
  }

  const visibleText = stripFormatting(text).trim();
  const passiveOnlyRegex = new RegExp(
    String.raw`^(?:${passiveAlternation})(?:(?:\s*[,\n.]\s*|\s+and\s+)(?:${passiveAlternation}))*\.?$`,
    'i'
  );
  const standaloneNumberedKeywordRegex = /^(?:Colossal \+\d+|Overload: \(\d+\)|Spell Damage \+\d+)$/i;
  const standaloneKeywordRegex = new RegExp(
    String.raw`^(?:${KEYWORDS.map(escapeRegExp).join('|')})$`,
    'i'
  );
  if (
    visibleText &&
    !/[.!?]$/.test(visibleText) &&
    !passiveOnlyRegex.test(visibleText) &&
    !standaloneNumberedKeywordRegex.test(visibleText) &&
    !standaloneKeywordRegex.test(visibleText)
  ) {
    addMistake({
      id: 'missing_final_punctuation',
      message: 'Card text normally ends with punctuation unless it is only a keyword line.',
      fixLabel: 'Add a final period',
      apply: applyField('Text', (value) => {
        const trimmed = value.trimEnd();
        const trailingTags = trimmed.match(new RegExp(String.raw`((?:${FORMAT_TAG_SOURCE})*)$`, 'i'))?.[1] ?? '';
        const body = trailingTags ? trimmed.slice(0, -trailingTags.length) : trimmed;
        return `${body}.${trailingTags}`;
      }),
      severity: 'warning'
    });
  }

  // A bare imperative is valid on a Spell, but usually incomplete on a
  // Minion. Restrict this heuristic to cards whose type is explicitly known,
  // and report the missing condition without guessing the intended trigger.
  const rawCardType = String(
    cardParams.Type ??
    cardParams.CardType ??
    cardParams.type ??
    cardParams.cardType ??
    ''
  ).trim();
  const isMinion = normalizeLookup(rawCardType) === 'minion';

  if (isMinion && visibleText) {
    const leadingPassivePrefixRegex = new RegExp(
      String.raw`^(?:(?:${passiveAlternation})(?:\s*[,\n.]\s*|\s+and\s+))+`,
      'i'
    );
    const effectAfterStaticKeywords = visibleText.replace(leadingPassivePrefixRegex, '').trimStart();
    const immediateEffectRegex = new RegExp(
      String.raw`^(${IMMEDIATE_EFFECT_VERBS.map(escapeRegExp).join('|')})\b`,
      'i'
    );
    const immediateEffect = effectAfterStaticKeywords.match(immediateEffectRegex);

    if (immediateEffect) {
      addMistake({
        id: 'missing_minion_trigger',
        message: `This Minion starts an immediate “${immediateEffect[1]}” effect without saying when it resolves. Add a condition such as “Battlecry:”, “Deathrattle:”, or another intended trigger.`,
        field: 'Text',
        severity: 'warning'
      });
    }
  }

  // These mechanics have matching visual banners in this creator. Validate
  // both halves, but only infer the mechanic from an actual keyword marker —
  // never from an incidental mention in card text.
  for (const { keyword, banner } of BANNER_KEYWORDS) {
    const idKeyword = keyword.toLocaleLowerCase('en-US');
    const hasKeywordText = hasOwnBannerKeyword(text, keyword);
    const hasKeywordBanner = cardParams.Banner === banner;

    if (hasKeywordBanner && !hasKeywordText) {
      addMistake({
        id: `${idKeyword}_banner_missing_text`,
        message: `The ${keyword} banner is enabled, but the card text has no ${keyword} keyword marker.`,
        fixLabel: `Add “${keyword}” to the text`,
        apply: (params) => {
          const currentText = String(params.Text ?? '');
          return {
            ...params,
            Text: currentText ? `${keyword}\n${currentText}` : keyword
          };
        },
        severity: 'error'
      });
    } else if (hasKeywordText && !hasKeywordBanner) {
      addMistake({
        id: `${idKeyword}_text_missing_banner`,
        message: `The card text marks this as a ${keyword} card, but its banner is not enabled.`,
        fixLabel: `Enable the ${keyword} banner`,
        apply: (params) => ({ ...params, Banner: banner }),
        field: 'Banner',
        severity: 'error'
      });
    }
  }

  // Card titles: fix whitespace and clear lower-case title words, while
  // preserving acronyms, intentional all-caps words, and mixed-case names.
  if (!title.trim()) {
    addMistake({
      id: 'missing_title',
      message: 'A Hearthstone card needs a title.',
      field: 'Title',
      severity: 'error'
    });
  } else {
    const suggestedTitle = titleCaseSuggestion(title);
    if (suggestedTitle !== title) {
      addMistake({
        id: 'title_format',
        message: 'The card title has nonstandard spacing or capitalization.',
        fixLabel: `“${title}” → “${suggestedTitle}”`,
        apply: (params) => ({ ...params, Title: titleCaseSuggestion(String(params.Title ?? '')) }),
        field: 'Title',
        severity: 'warning'
      });
    }
  }

  // Tribe/minion-type validation. Spell schools are deliberately not accepted
  // here; they are a separate card property in Hearthstone.
  const tribe = String(cardParams.Tribe ?? '').trim();
  if (tribe) {
    const knownNonMinionTypes = new Set(['weapon', 'hero', 'location', 'hero power', 'heropower']);
    if (knownNonMinionTypes.has(normalizeLookup(rawCardType))) {
      addMistake({
        id: 'minion_type_on_non_minion',
        message: `A ${rawCardType} cannot have a minion type.`,
        fixLabel: 'Remove the minion type',
        apply: (params) => ({ ...params, Tribe: '' }),
        field: 'Tribe',
        severity: 'error'
      });
    }

    const typeMap = canonicalMap(MINION_TYPES);
    const rawTypes = tribe.split(/\s*[,/&]\s*|\s{2,}/).filter(Boolean);
    const types = rawTypes.length > 1 ? rawTypes : (typeMap.has(normalizeLookup(tribe)) ? [tribe] : tribe.split(/\s+/));
    const canonicalTypes = types.map((type) => typeMap.get(normalizeLookup(type)));

    canonicalTypes.forEach((canonical, index) => {
      const raw = types[index];
      if (!canonical) {
        addMistake({
          id: `unknown_minion_type_${normalizeLookup(raw).replace(/\W+/g, '_') || index}`,
          message: `Unrecognized minion type: “${raw}”.`,
          field: 'Tribe',
          severity: 'warning'
        });
      } else if (raw !== canonical) {
        addMistake({
          id: `minion_type_field_case_${canonical.toLocaleLowerCase('en-US')}`,
          message: `The minion type should be written as “${canonical}”.`,
          fixLabel: `“${raw}” → “${canonical}”`,
          apply: applyField('Tribe', (value) => value.replace(new RegExp(`\\b${escapeRegExp(raw)}\\b`, 'g'), canonical)),
          field: 'Tribe',
          severity: 'error'
        });
      }
    });

    const recognizedTypes = canonicalTypes.filter(Boolean);
    if (new Set(recognizedTypes).size !== recognizedTypes.length) {
      addMistake({
        id: 'duplicate_minion_type',
        message: 'A dual-type minion cannot have the same minion type twice.',
        field: 'Tribe',
        severity: 'error'
      });
    }

    if (recognizedTypes.includes('All') && recognizedTypes.length > 1) {
      addMistake({
        id: 'all_minion_type_combined',
        message: '“All” cannot be combined with another minion type.',
        field: 'Tribe',
        severity: 'error'
      });
    } else if (recognizedTypes.length > 2) {
      addMistake({
        id: 'too_many_minion_types',
        message: 'Official Hearthstone cards have at most two minion types (or the “All” type).',
        field: 'Tribe',
        severity: 'warning'
      });
    }
  }

  // Accept normal and dual-class values, including spaced UI labels for the
  // two multi-word classes.
  const rawClass = String(cardParams.Class ?? '').trim();
  if (!rawClass) {
    addMistake({
      id: 'missing_class',
      message: 'A Hearthstone card needs a class (Neutral is valid).',
      field: 'Class',
      severity: 'error'
    });
  } else {
    const classMap = canonicalMap(CLASSES);
    classMap.set('death knight', 'DeathKnight');
    classMap.set('demon hunter', 'DemonHunter');
    const rawClasses = rawClass.split(/\s*[,/&]\s*/).filter(Boolean);
    const canonicalClasses = rawClasses.map((value) => classMap.get(normalizeLookup(value)));

    canonicalClasses.forEach((canonical, index) => {
      const raw = rawClasses[index];
      if (!canonical) {
        addMistake({
          id: `unknown_class_${normalizeLookup(raw).replace(/\W+/g, '_') || index}`,
          message: `Unrecognized class: “${raw}”.`,
          field: 'Class',
          severity: 'warning'
        });
      } else if (raw !== canonical) {
        addMistake({
          id: `class_field_case_${canonical.toLocaleLowerCase('en-US')}`,
          message: `The class value should be “${canonical}”.`,
          fixLabel: `“${raw}” → “${canonical}”`,
          apply: applyField('Class', (value) => value.replace(raw, canonical)),
          field: 'Class',
          severity: 'error'
        });
      }
    });

    const recognizedClasses = canonicalClasses.filter(Boolean);
    if (new Set(recognizedClasses).size !== recognizedClasses.length) {
      addMistake({
        id: 'duplicate_class',
        message: 'A dual-class card cannot belong to the same class twice.',
        fixLabel: `Use “${[...new Set(recognizedClasses)].join(', ')}”`,
        apply: (params) => {
          const values = String(params.Class ?? '').split(/\s*[,\/&]\s*/).filter(Boolean);
          const seen = new Set();
          const unique = values.filter((value) => {
            const key = classMap.get(normalizeLookup(value)) ?? normalizeLookup(value);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          return { ...params, Class: unique.join(', ') };
        },
        field: 'Class',
        severity: 'error'
      });
    }

    if (recognizedClasses.includes('Neutral') && recognizedClasses.length > 1) {
      addMistake({
        id: 'neutral_multiclass',
        message: 'Neutral cannot be combined with another class.',
        field: 'Class',
        severity: 'error'
      });
    } else if (recognizedClasses.length > 2) {
      addMistake({
        id: 'too_many_classes',
        message: 'Official multi-class cards belong to two classes.',
        field: 'Class',
        severity: 'warning'
      });
    }
  }

  return mistakes;
};

export const HEARTHSTONE_CARD_CHECK_DATA = Object.freeze({
  classes: Object.freeze([...CLASSES]),
  keywords: Object.freeze([...KEYWORDS]),
  minionTypes: Object.freeze([...MINION_TYPES]),
  spellSchools: Object.freeze([...SPELL_SCHOOLS])
});
